import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { JobStatus } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { StarRating } from '../../components/ratings/StarRating';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Field,
  Muted,
  Stack,
  Strong,
  Textarea,
} from '../../components/ui';
import {
  useServiceJobRatingQuery,
  useSubmitServiceCustomerRatingMutation,
  useSubmitServiceWorkerRatingMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import {
  CUSTOMER_REVIEW_CATEGORIES,
  scoresComplete,
  serviceReviewClosesAt,
  serviceReviewView,
  WORKER_REVIEW_CATEGORIES,
  type ServiceReviewer,
} from './serviceReviews';

function ScoreRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value?: number;
  onChange: (value: number) => void;
}) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
      data-testid={`service-review-score-${label.toLowerCase().replace(/\W+/g, '-')}`}
    >
      <div style={{ minWidth: 0 }}>
        <Strong>{label}</Strong>
        {hint ? (
          <Muted $size="sm" style={{ display: 'block' }}>
            {hint}
          </Muted>
        ) : null}
      </div>
      <StarRating rating={value ?? 0} onRatingChange={onChange} size="sm" showLabel={false} />
    </div>
  );
}

function ReviewModal({
  bookingId,
  reviewer,
  otherName,
  onClose,
  onDone,
}: {
  bookingId: string;
  reviewer: ServiceReviewer;
  otherName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const categories =
    reviewer === 'customer' ? CUSTOMER_REVIEW_CATEGORIES : WORKER_REVIEW_CATEGORIES;
  const keys = useMemo(() => ['overall', ...categories.map(c => c.key)], [categories]);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [review, setReview] = useState('');
  const [privateNote, setPrivateNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitCustomer, { loading: savingCustomer }] = useSubmitServiceCustomerRatingMutation();
  const [submitWorker, { loading: savingWorker }] = useSubmitServiceWorkerRatingMutation();

  useEffect(() => setError(null), [scores]);

  const submit = async () => {
    setError(null);
    try {
      if (reviewer === 'customer') {
        await submitCustomer({
          variables: {
            input: {
              bookingId,
              overall: scores.overall,
              thoroughness: scores.thoroughness,
              punctuality: scores.punctuality,
              communication: scores.communication,
              photoCompleteness: scores.photoCompleteness,
              review: review.trim() || null,
              privateNote: privateNote.trim() || null,
            },
          },
        });
      } else {
        await submitWorker({
          variables: {
            input: {
              bookingId,
              overall: scores.overall,
              accuracy: scores.accuracy,
              access: scores.access,
              communication: scores.communication,
              review: review.trim() || null,
            },
          },
        });
      }
      toast.success('Thanks for your review');
      onDone();
    } catch (e) {
      setError(stayErrorMessage(e, 'Your review could not be saved.'));
    }
  };

  const setScore = (key: string) => (value: number) =>
    setScores(prev => ({ ...prev, [key]: value }));

  return (
    <Modal isOpen onClose={onClose} title="Write a review" modalWidth="520px">
      <Stack $gap={6} data-testid="service-review-modal">
        <Stack $gap={4}>
          <Strong style={{ fontSize: 20 }}>
            {reviewer === 'customer' ? `How did ${otherName} do?` : `How was ${otherName}?`}
          </Strong>
          <Muted $size="sm">
            {otherName} sees your review once they write theirs, or after 14 days.
          </Muted>
        </Stack>
        <ScoreRow label="Overall" value={scores.overall} onChange={setScore('overall')} />
        {categories.map(category => (
          <ScoreRow
            key={category.key}
            label={category.label}
            hint={category.hint}
            value={scores[category.key]}
            onChange={setScore(category.key)}
          />
        ))}
        <Field
          label="Public review"
          htmlFor="service-review-public"
          hint={
            reviewer === 'customer'
              ? 'Optional. Shown on the worker’s page with your first name.'
              : 'Optional. Other workers see it before taking a job here.'
          }
        >
          <Textarea
            id="service-review-public"
            rows={4}
            maxLength={1000}
            value={review}
            onChange={e => setReview(e.target.value)}
          />
        </Field>
        {reviewer === 'customer' && (
          <Field
            label="Private feedback"
            htmlFor="service-review-private"
            hint="Optional. Only our team reads it."
          >
            <Textarea
              id="service-review-private"
              rows={2}
              maxLength={1000}
              value={privateNote}
              onChange={e => setPrivateNote(e.target.value)}
            />
          </Field>
        )}
        {error ? (
          <Alert tone="error" title="Not saved">
            {error}
          </Alert>
        ) : null}
        <Button
          text="Submit review"
          fullWidth
          isDisabled={!scoresComplete(scores, keys)}
          isLoading={savingCustomer || savingWorker}
          onClick={submit}
          dataTestId="service-review-submit"
        />
      </Stack>
    </Modal>
  );
}

/**
 * The review card on a finished job (spec 019 R16): the customer rates the
 * worker (W21); the worker or the Provider rates the customer (W19). Blind:
 * the other side's words show once both are in or 14 days pass.
 */
export function ServiceReviewCard({
  bookingId,
  reviewer,
  jobStatus,
  completedAt,
  otherName,
  zone,
}: {
  bookingId: string;
  reviewer: ServiceReviewer;
  jobStatus: JobStatus | null | undefined;
  completedAt: string | null | undefined;
  otherName: string;
  zone?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const finished = serviceReviewClosesAt(jobStatus, completedAt) !== null;
  const { data, refetch } = useServiceJobRatingQuery({
    variables: { bookingId },
    skip: !finished,
    fetchPolicy: 'cache-and-network',
  });
  if (!finished) return null;
  const rating = data?.getTripRating ?? null;
  const mine = reviewer === 'customer' ? rating?.guestRatedAt : rating?.hostRatedAt;
  const theirsAt = reviewer === 'customer' ? rating?.hostRatedAt : rating?.guestRatedAt;
  const view = serviceReviewView({
    jobStatus,
    completedAt,
    mine,
    theirs: theirsAt,
    otherName,
    zone,
  });
  if (view.kind === 'hidden' || (!data && !rating)) return null;
  const myWords =
    reviewer === 'customer' ? rating?.serviceCustomerRating : rating?.serviceWorkerRating;
  const theirWords =
    reviewer === 'customer' ? rating?.serviceWorkerRating : rating?.serviceCustomerRating;

  return (
    <Card data-testid="service-review">
      <CardHeader
        title={reviewer === 'customer' ? 'Review this job' : `Review ${otherName}`}
        description={view.text}
      />
      <CardBody>
        <Stack $gap={4}>
          {view.kind === 'write' && (
            <div>
              <Button
                type="button"
                text="Write a review"
                onClick={() => setOpen(true)}
                dataTestId="service-review-write"
              />
            </div>
          )}
          {myWords && (
            <Stack $gap={4} data-testid="service-review-mine">
              <Strong>You gave {myWords.overall} of 5</Strong>
              {myWords.review ? <Muted>“{myWords.review}”</Muted> : null}
            </Stack>
          )}
          {theirWords && (
            <Stack $gap={4} data-testid="service-review-theirs">
              <Strong>
                {otherName} gave {reviewer === 'customer' ? 'you' : 'the job'} {theirWords.overall}{' '}
                of 5
              </Strong>
              {theirWords.review ? <Muted>“{theirWords.review}”</Muted> : null}
            </Stack>
          )}
        </Stack>
      </CardBody>
      {open && (
        <ReviewModal
          bookingId={bookingId}
          reviewer={reviewer}
          otherName={otherName}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            void refetch();
          }}
        />
      )}
    </Card>
  );
}
